/**
 * Page registry — плоский список страниц UI для поиска ⌘K.
 *
 * ⚠️ ИСТОЧНИК ПРАВДЫ — src/composables/navModel.ts (тот же, из которого
 * строится AppSidebar.vue). Здесь мы лишь переводим i18n-ключи в строки и
 * упаковываем в PageEntry + добавляем простой fuzzy-ранкер. Раньше список
 * вёлся вручную и отставал от боковой панели — новые разделы («Опросы»,
 * «Задачи оператору», …) не находились через ⌘K (баг B25). Теперь, добавив
 * пункт в navModel, он автоматически появляется и в меню, и в поиске.
 */

import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { ALL_NAV_ITEMS } from "./navModel";

export interface PageEntry {
  /** Router path. */
  path: string;
  /** Локализованное название страницы. */
  label: string;
  /** Локализованное название раздела (для группировки результатов). */
  section: string;
  /** Альтернативные слова для поиска (на случай если пользователь ищет по англицизму). */
  aliases?: string[];
}

export function usePageRegistry() {
  const { t } = useI18n();

  // Строим реестр из единой модели навигации — никакого ручного дублирования.
  const pages = computed<PageEntry[]>(() =>
    ALL_NAV_ITEMS.map((item) => ({
      path: item.path,
      label: t(item.labelKey),
      section: t(item.sectionKey),
      aliases: item.aliases,
    })),
  );

  /**
   * Simple substring fuzzy ranker:
   *   1) exact-prefix on label → 0
   *   2) substring in label → 1
   *   3) substring in alias → 2
   *   4) substring in section → 3
   *   no match → null
   */
  function search(query: string, limit = 12): PageEntry[] {
    const q = query.trim().toLowerCase();
    if (!q) return pages.value.slice(0, limit); // empty query: show top
    const scored: Array<{ page: PageEntry; score: number }> = [];
    for (const p of pages.value) {
      const label = p.label.toLowerCase();
      const section = p.section.toLowerCase();
      let score: number | null = null;
      if (label.startsWith(q)) score = 0;
      else if (label.includes(q)) score = 1;
      else if (p.aliases?.some((a) => a.toLowerCase().includes(q))) score = 2;
      else if (section.includes(q)) score = 3;
      if (score !== null) scored.push({ page: p, score });
    }
    scored.sort((a, b) => a.score - b.score || a.page.label.localeCompare(b.page.label, "ru"));
    return scored.slice(0, limit).map((s) => s.page);
  }

  return { pages, search };
}
